import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { auth } from "../services/firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";

// Import asset and module styles
import meetlyIcon from "../assets/Meetly-icon.png";
import styles from "./Login.module.css";

export default function AuthScreen({ onLoginSuccess }) {
  // --- STATE MANAGEMENT ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // States: 'none', 'login', or 'register'
  const [authMode, setAuthMode] = useState("none");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // --- GOOGLE AUTH HANDLER ---
  const handleGoogleSignup = async () => {
    setError("");
    setLoading(true);
    const provider = new GoogleAuthProvider();

    try {
      await signInWithPopup(auth, provider);
      setLoading(false);
      if (onLoginSuccess) onLoginSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // --- EMAIL/PASSWORD AUTH HANDLER ---
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (authMode === "register") {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      setLoading(false);
      if (onLoginSuccess) onLoginSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.authCard}>
        {/* Back arrow button shown when inside the login/register form */}
        {authMode !== "none" && (
          <button
            type="button"
            onClick={() => {
              setAuthMode("none");
              setError("");
            }}
            className={styles.backArrow}
          >
            ← Back
          </button>
        )}

        {/* Hero banner section with background watermark image */}
        {authMode === "none" && (
          <div className={styles.heroBanner}>
            <img
              src={meetlyIcon}
              alt="Meetly Watermark"
              className={styles.heroWatermark}
            />
            <h1 className={styles.heroTitle}>
              Your community is waiting for you!
            </h1>
          </div>
        )}

        {/* Error message display if any auth failure occurs */}
        {error && <div className={styles.errorMessage}>{error}</div>}

        {/* Conditional rendering for loading spinner, forms, or main buttons */}
        {loading ? (
          <div className={styles.loaderContainer}>
            <Loader2 className={styles.spinner} size={32} />
          </div>
        ) : authMode !== "none" ? (
          /* Email / Password Form View */
          <form onSubmit={handleEmailAuth} className={styles.authForm}>
            <h2 className={styles.formTitle}>
              {authMode === "register" ? "Create Account" : "Welcome Back"}
            </h2>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Email Address</label>
              <input
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={styles.authInput}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={styles.authInput}
              />
            </div>

            <button type="submit" className={styles.emailButton}>
              {authMode === "register" ? "Sign Up" : "Log In"}
            </button>
          </form>
        ) : (
          /* Main Social & Email Actions Container */
          <div className={styles.actionButtonsContainer}>
            {/* Continue with Google Button */}
            <button
              onClick={handleGoogleSignup}
              className={styles.googleButton}
            >
              <svg className={styles.googleIcon} viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
                />
                <path
                  fill="#34A853"
                  d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.19v3.15C3.17 21.36 7.23 24 12 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.19C.43 8.1 0 9.8 0 12s.43 3.9 1.19 5.42l4.09-3.15z"
                />
                <path
                  fill="#EA4335"
                  d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.23 0 3.17 2.64 1.19 6.58l4.09 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                />
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className={styles.divider}>
              <span>Or</span>
            </div>

            {/* Sign up with email Button */}
            <button
              onClick={() => setAuthMode("register")}
              className={styles.emailButton}
            >
              Sign up with your email
            </button>

            {/* Log in text link footer */}
            <div className={styles.loginLinkContainer}>
              <button
                type="button"
                onClick={() => setAuthMode("login")}
                className={styles.textLoginLink}
              >
                Log in with your email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
